import { useCallback } from "react";
import {
  IFiltrosProjeto,
  IListaCEP,
  IListaCidades,
  IListaDeduplicacao,
  IProjeto,
  IProjetoTable,
  IRelatorioProjeto,
} from "../../../application/models";
import { useAxiosInstances } from "../../axios-instance";
import { ITravaConsumo } from "../../../application/models/geo-veles/ITravaConsumo";
import { useAppSelector } from "../../../presentation/hooks/store-hooks";

export const useProjetosApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();
  const { auth } = useAppSelector((state) => state);

  const getProjetosApi = useCallback(
    async (data: {
      pagination: {
        page: number;
        pageSize: number;
      };
      filter: {
        idUsuario: number;
        cliente?: string;
        inclusaoMax?: Date;
        inclusaoMin?: Date;
        searchTerm?: string;
        ativo?: boolean;
        usuario?: string;
        loginUsuario?: string;
      };
    }): Promise<{
      projetos: IProjetoTable[];
      page: number;
      limit: number;
      total: number;
      listasDeduplicacao?: IListaDeduplicacao[];
      listasCEP?: IListaCEP[];
      listasCidade?: IListaCidades[];
    }> => {
      try {

        const result = await axiosAuthInstance.get("/geo-veles/projeto", {
          params: {
            idUsuario: data.filter.idUsuario,
            cliente: data.filter.cliente,
            inclusaoMax: data.filter.inclusaoMax,
            inclusaoMin: data.filter.inclusaoMin,
            limit: data.pagination.pageSize,
            page: data.pagination.page,
            nome: data.filter.searchTerm,
            ativo: data.filter.ativo,
            usuario: data.filter.usuario,
            loginUsuario: data.filter.loginUsuario,
          },
        });  

        return {
          limit: result.data.limit,
          page: result.data.page,
          projetos: result.data.projetos, 
          total: result.data.total,
          listasDeduplicacao: result.data.listasDeduplicacao || [],
          listasCEP: result.data.listasCEP || [],
          listasCidade: result.data.listasCidade || [],
        };

      } catch (error) {
        throw error;
      }
    },
    
    [axiosAuthInstance]
  );

  const getProjetoByIdApi = useCallback(
    async (data: { idVelesGeoProjeto: number }): Promise<IProjeto> => {
      try {
        const result = await axiosAuthInstance.get(
          `/geo-veles/projeto/${data.idVelesGeoProjeto}`
        );
       
        return result.data.projeto;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const verifyNomeProjetoExistsApi = useCallback(
    async (data: { nome: string; idUsuario: number; idVelesGeoProjeto?: number }): Promise<boolean> => {
      try {
        await axiosAuthInstance.get(
          "/geo-veles/projeto/verifyNome",
          {
            params: {
              nome: data.nome,
              idUsuario: data.idUsuario,
              idVelesGeoProjeto: data.idVelesGeoProjeto
            },
          }
        );
        return true;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const createProjetoApi = useCallback(
    async (data: { nome: string; idUsuario: number }): Promise<IProjeto> => {
      try {
        const result = await axiosAuthInstance.post("/geo-veles/projeto", {
          idUsuario: data.idUsuario,
          nome: data.nome,
        });

        if (result.data) {
          return result.data.projeto;
        } else {
          throw new Error("Erro ao criar projeto");
        }
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const updateProjetoNomeAndAtivoApi = useCallback(
    async (data: {
      idVelesGeoProjeto: number;
      nome: string;
      ativo: boolean;
    }): Promise<IProjeto> => {
      try {
        const result = await axiosAuthInstance.put("/geo-veles/projeto", {
          idVelesGeoProjeto: data.idVelesGeoProjeto,
          ativo: data.ativo,
          nome: data.nome,
        });

        return result.data.projeto;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const updateFiltrosProjetoApi = useCallback(
    async (data: {
      filtros: IFiltrosProjeto;
      idVelesGeoProjeto: number;
    }): Promise<{
      projeto: IProjeto;
      relatorioProjeto: IRelatorioProjeto;
    }> => {
      
      try {
        const result = await axiosAuthInstance.put(
          "/geo-veles/projeto/filtros",
          {
            idVelesGeoProjeto: data.idVelesGeoProjeto,
            filtros: data.filtros,
          },

          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
        );

        return {
          projeto: result.data.projeto,
          relatorioProjeto: result.data.relatorioProjeto,
        };
      } catch (error) {
        throw error;
      }
    },
    [auth.token, axiosAuthInstance]
  );

  const getTravaConsumoApi = useCallback(
    async (data: {
      IdCliente: number;
      IdUsuario: number;
    }): Promise<ITravaConsumo> => {
      try {
        const result = await axiosAuthInstance.get(
          `/geo-veles/trava-consumo/${data.IdCliente}/${data.IdUsuario}`
        );
        return result.data;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const duplicateProjetoApi = useCallback(
    async (data: {
      IdVelesGeoProjeto: number;
      nome: string;
      IdUsuario: number;
    }): Promise<IProjeto> => {
      try {
        const result = await axiosAuthInstance.post(
          "/geo-veles/projeto/duplicate",
          {
            IdVelesGeoProjeto: data.IdVelesGeoProjeto,
            nome: data.nome,
            IdUsuario: data.IdUsuario,
          }
        );
        if (result.data) {
          return result.data.projeto;
        } else {
          throw new Error("Erro ao criar projeto");
        }
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );  

  return {
    getProjetosApi,
    getProjetoByIdApi,
    createProjetoApi,
    updateProjetoNomeAndAtivoApi,
    updateFiltrosProjetoApi,
    getTravaConsumoApi,
    duplicateProjetoApi,
    verifyNomeProjetoExistsApi,
  };
};
